import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/Storage';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useLogin } from '../../contexts/LoginContext';
import { notificacoesApi } from './services/notificacoesApi';
import { INotificacao } from './types';
import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

export default function NotificacoesMenu() {
  const theme = useTheme();
  const [login] = useLogin();
  const clienteID = login.usuario?.usuarioCliente?.clienteID;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notificacoes, setNotificacoes] = useState<INotificacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState<'todas' | 'alertas' | 'financeiro'>('todas');
  const [hoveredID, setHoveredID] = useState<string | null>(null);

  const carregarNotificacoes = React.useCallback(async () => {
    if (!clienteID || !login.accessToken) return;
    try {
      setLoading(true);
      const items = await notificacoesApi.listar(clienteID, login.accessToken);
      setNotificacoes(items);
    } catch (error) {
      console.error("Erro ao carregar notificações no frontend:", error);
    } finally {
      setLoading(false);
    }
  }, [clienteID, login.accessToken]);

  // Carregar notificações ao montar e a cada 60 segundos
  useEffect(() => {
    carregarNotificacoes();
    const interval = setInterval(carregarNotificacoes, 60000);
    return () => clearInterval(interval);
  }, [carregarNotificacoes]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Recarregar sempre que abrir para garantir dados frescos
    carregarNotificacoes();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notificacoes-popover' : undefined;

  // Filtros
  const notificacoesFiltradas = notificacoes.filter(n => {
    if (filtro === 'todas') return true;
    if (filtro === 'alertas') return n.categoria === 'backup' || n.categoria === 'hardware';
    if (filtro === 'financeiro') return n.categoria === 'financeiro';
    return true;
  });

  const naoLidasCount = notificacoes.filter(n => !n.lida).length;

  const handleMarcarComoLida = async (nID: string, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    try {
      await notificacoesApi.marcarComoLida(nID, clienteID, login.accessToken);
      setNotificacoes(prev =>
        prev.map(n => (n.id === nID ? { ...n, lida: true } : n))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleExcluir = async (nID: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await notificacoesApi.excluir(nID, clienteID, login.accessToken);
      setNotificacoes(prev => prev.filter(n => n.id !== nID));
    } catch (error) {
      console.error(error);
    }
  };

  const handleMarcarTodasComoLidas = async () => {
    if (!clienteID || !login.accessToken) return;
    try {
      await notificacoesApi.marcarTodasComoLidas(clienteID, login.accessToken);
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
    } catch (error) {
      console.error(error);
    }
  };

  const handleLimparTodas = async () => {
    if (!clienteID || !login.accessToken) return;
    try {
      await notificacoesApi.limparTodas(clienteID, login.accessToken);
      setNotificacoes([]);
    } catch (error) {
      console.error(error);
    }
  };

  // Ícones e Cores por Categoria
  const obterAvatarConfig = (categoria: string, prioridade: string) => {
    const isDark = theme.palette.mode === 'dark';
    
    switch (categoria) {
      case 'backup':
        if (prioridade === 'alta') {
          return {
            icon: <ErrorOutlineIcon sx={{ color: '#EF4444' }} />,
            bg: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)'
          };
        }
        return {
          icon: <CheckCircleOutlineIcon sx={{ color: '#10B981' }} />,
          bg: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)'
        };
      case 'financeiro':
        return {
          icon: <MonetizationOnIcon sx={{ color: '#3B82F6' }} />,
          bg: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'
        };
      case 'hardware':
        return {
          icon: <StorageIcon sx={{ color: '#F59E0B' }} />,
          bg: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)'
        };
      case 'sistema':
      default:
        // Notificações de novidades/sistema recebem ícone comercial chamativo
        return {
          icon: <CampaignIcon sx={{ color: '#8B5CF6' }} />,
          bg: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)'
        };
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} color="inherit" sx={{ paddingLeft: theme.spacing(4) }}>
        <Badge badgeContent={naoLidasCount} color="error" max={99}>
          {naoLidasCount > 0 ? (
            <NotificationsIcon sx={{ color: theme.palette.text.secondary }} />
          ) : (
            <NotificationsNoneIcon sx={{ color: theme.palette.text.secondary }} />
          )}
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 520,
            borderRadius: '12px',
            boxShadow: theme.shadows[10],
            border: `1px solid ${theme.palette.divider}`,
            mt: 1.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Cabeçalho */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Notificações
            </Typography>
            {naoLidasCount > 0 && (
              <Chip
                label={`${naoLidasCount} novas`}
                size="small"
                color="primary"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  color: '#FFF'
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {notificacoes.length > 0 && (
              <>
                <Tooltip title="Marcar todas como lidas">
                  <IconButton size="small" onClick={handleMarcarTodasComoLidas} sx={{ color: 'text.secondary' }}>
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Limpar todas as notificações">
                  <IconButton size="small" onClick={handleLimparTodas} sx={{ color: 'text.secondary' }}>
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        {/* Filtros */}
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1 }}>
          <Chip
            label="Todas"
            size="small"
            clickable
            color={filtro === 'todas' ? 'primary' : 'default'}
            variant={filtro === 'todas' ? 'filled' : 'outlined'}
            onClick={() => setFiltro('todas')}
            sx={{ fontSize: '0.75rem', height: 26 }}
          />
          <Chip
            label="Alertas"
            size="small"
            clickable
            color={filtro === 'alertas' ? 'primary' : 'default'}
            variant={filtro === 'alertas' ? 'filled' : 'outlined'}
            onClick={() => setFiltro('alertas')}
            sx={{ fontSize: '0.75rem', height: 26 }}
          />
          <Chip
            label="Financeiro"
            size="small"
            clickable
            color={filtro === 'financeiro' ? 'primary' : 'default'}
            variant={filtro === 'financeiro' ? 'filled' : 'outlined'}
            onClick={() => setFiltro('financeiro')}
            sx={{ fontSize: '0.75rem', height: 26 }}
          />
        </Box>

        <Divider />

        {/* Lista */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 380 }}>
          {loading && notificacoes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notificacoesFiltradas.length === 0 ? (
            <Box sx={{ py: 6, px: 3, textAlignment: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  mb: 2
                }}
              >
                <NotificationsNoneIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Tudo limpo por aqui!
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Nenhuma notificação encontrada nesta categoria.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notificacoesFiltradas.map((n, index) => {
                const config = obterAvatarConfig(n.categoria, n.prioridade);
                const isGlobalVersion = n.titulo.includes("Versão 2.0");

                return (
                  <React.Fragment key={n.id}>
                    <ListItem
                      alignItems="flex-start"
                      onMouseEnter={() => setHoveredID(n.id)}
                      onMouseLeave={() => setHoveredID(null)}
                      onClick={() => !n.lida && handleMarcarComoLida(n.id)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        cursor: 'pointer',
                        backgroundColor: n.lida
                          ? 'transparent'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(139, 92, 246, 0.05)'
                          : 'rgba(139, 92, 246, 0.03)',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                        },
                        // Se for a versão global, damos uma leve borda estilizada
                        borderLeft: isGlobalVersion
                          ? `4px solid ${theme.palette.secondary.main}`
                          : 'none',
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 46 }}>
                        <Avatar sx={{ width: 36, height: 36, backgroundColor: config.bg }}>
                          {config.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: n.lida ? 500 : 700,
                                color: isGlobalVersion ? 'secondary.main' : 'text.primary',
                                fontSize: '0.85rem',
                                lineHeight: '1.2rem'
                              }}
                            >
                              {n.titulo}
                            </Typography>
                            {!n.lida && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.primary.main,
                                  flexShrink: 0,
                                  mt: 0.5
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.78rem',
                                display: 'block',
                                lineHeight: '1.1rem',
                                mb: 0.5
                              }}
                            >
                              {n.mensagem}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                              {moment(n.dataCadastro).fromNow()}
                            </Typography>
                          </Box>
                        }
                      />

                      {/* Ações inline no hover */}
                      {hoveredID === n.id && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: 0.5,
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: '4px',
                            boxShadow: theme.shadows[2],
                            p: 0.5,
                            border: `1px solid ${theme.palette.divider}`,
                            zIndex: 1
                          }}
                        >
                          {!n.lida && (
                            <Tooltip title="Lida">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMarcarComoLida(n.id, e)}
                                sx={{ color: 'success.main', p: 0.2 }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={(e) => handleExcluir(n.id, e)}
                              sx={{ color: 'error.main', p: 0.2 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </ListItem>
                    {index < notificacoesFiltradas.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
